import {useAuth0} from '@auth0/auth0-react';
import {RealtimeEventType} from '@sollapay/enums';
import {
  createRecoveringTokenGetter,
  getCurrentPath,
  useLoginContextGetter,
  useOptionalTheme
} from '@sollapay/web-core';
import {useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {config} from '@/app';

import type {RealtimeEvent} from '@sollapay/types';

/**
 * Module-level singleton: one EventSource shared across all useEventStream callers.
 * Multiple hooks subscribe handlers; the connection opens on first subscriber and
 * closes when the last subscriber unmounts.
 */
type EventHandler = (event: RealtimeEvent) => void;

const subscribers = new Set<EventHandler>();
let es: EventSource | null = null;
let connecting = false;
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let tokenGetter: (() => Promise<string>) | null = null;

function broadcast(event: RealtimeEvent): void {
  for (const handler of subscribers) {
    try {
      handler(event);
    } catch {
      // Ignore individual handler errors so one bad handler can't kill the rest
    }
  }
}

function scheduleReconnect(): void {
  retryTimeoutId = setTimeout(() => {
    retryTimeoutId = null;
    void openConnection();
  }, 5_000);
}

async function openConnection(): Promise<void> {
  if (!tokenGetter || es || connecting) return;
  connecting = true;
  try {
    const token = await tokenGetter();
    if (!tokenGetter) return; // last subscriber left while awaiting token
    const url = `${config.apiUrl}/v1/api/events/stream?access_token=${encodeURIComponent(token)}`;
    es = new EventSource(url);
    for (const eventType of Object.values(RealtimeEventType)) {
      es.addEventListener(eventType, e => {
        try {
          const data = JSON.parse((e as MessageEvent).data) as RealtimeEvent;
          broadcast(data);
        } catch {
          // Ignore malformed event payloads
        }
      });
    }
    es.onerror = () => {
      es?.close();
      es = null;
      if (tokenGetter !== null) scheduleReconnect();
    };
  } catch {
    // getAccessTokenSilently failed — retry after delay
    if (tokenGetter !== null) scheduleReconnect();
  } finally {
    connecting = false;
  }
}

function closeConnection(): void {
  tokenGetter = null;
  if (retryTimeoutId !== null) {
    clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
  }
  es?.close();
  es = null;
}

export function useEventStream(handler: EventHandler): void {
  const {getAccessTokenSilently, loginWithRedirect} = useAuth0();
  const {i18n} = useTranslation();
  const theme = useOptionalTheme()?.theme;
  const getLoginContext = useLoginContextGetter();
  const themeRef = useRef(theme);
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
    themeRef.current = theme;
  });

  const getToken = useMemo(
    () =>
      createRecoveringTokenGetter({
        getAccessTokenSilently,
        loginWithRedirect,
        getReturnToPath: getCurrentPath,
        getUiLocale: () => i18n.language,
        getTheme: () => themeRef.current,
        getLoginContext
      }),
    [getAccessTokenSilently, loginWithRedirect, i18n, getLoginContext]
  );

  useEffect(() => {
    tokenGetter = getToken;
    const subscriber: EventHandler = event => handlerRef.current(event);
    subscribers.add(subscriber);
    if (subscribers.size === 1) void openConnection();
    return () => {
      subscribers.delete(subscriber);
      if (subscribers.size === 0) closeConnection();
    };
  }, [getToken]);
}
